import './../resources/javascripts/libs';
import angular from 'angular';
import ngRouter from 'angular-route';
import ngSanitize from 'angular-sanitize';
import ngTouch from 'angular-touch';
import ngSlick from 'angular-slick';
import ngTranslate from 'angular-translate';
import ngAnimate from 'angular-animate';
// module imports
import FileModule from './file';
import APIModule from './api';
import CacheModule from './cache';
import TransitionModule from './transition';
import LoadingModule from './loading';
import ModelModule from './model';
import FormatterModule from './formatter';
// stylesheet
import './../resources/stylesheets/intellifoundation/styles.scss';
// exports
import disableLogging from './config/logging.config';
import APP_ROUTES_DEFINITION from './config/app-route-definitions.config';
import APP_ERROR_CODES from './config/app-error-codes.config';
import './config/special-parameter.config';
import bootstrapApp, {loadBindings} from './bootstrap';
import translations from './config/translation.config';
import HomeComponent from './components/home/home.component';
import TechnologyComponent from './components/technology/technology.component';
import DistributorComponent from './components/distributor/distributor.component';
import BrowseComponent from './components/browse/browse.component';
import CompareComponent from './components/compare/compare.component';
import SelectedFiltersComponent from './components/selected-filters/selected-filters.component';
import HeaderController from './controller/header/header.controller';
import DistributorPricingModalController from './controller/modal/distributor-pricing-modal.controller';
import BuilderController from './controller/builder.controller';
import DefinitionController from './controller/definition.controller';
import ExternalSearchExecutionController from './controller/external-search-execution.controller';
import MessageController from './controller/message.controller';
import QueueController from './controller/queue.controller';
import SearchBarController from './controller/search-bar.controller';
import BuilderSidebarDirective from './directives/builder/builder.directive';
import MessageDirective from './directives/message/message.directive';
import QueueDirective from './directives/queue/queue.directive';
import BindHtmlCompileDirective from './directives/bind-html-compile.directive';
import CERepeatDirective from './directives/ce-repeat.directive';
import CheckImageDirective from './directives/check-image.directive';
import EqualHeightsDirective from './directives/equal-heights.directive';
import FocusDirective from './directives/focus.directive';
import LongStringFixerDirective from './directives/long-string-fixer.directive';
import StickyClassDirective from './directives/sticky-class.directive';
import TransitionEndDirective from './directives/transition-end.directive';
import DistributorsFilter from './filter/distributor.filter';
import EncodeURIComponentFilter from './filter/encode-uri-component.filter';
import EscapeFilter from './filter/escape.filter';
import FormatPartFilter from './filter/format-part.filter';
import StartsWithFilter from './filter/starts-with.filter';
import SleuthService from './service/sleuth.service';
import SelectedFiltersService from './service/selected-filters.service';
import SearchExecutorService from './service/search-executor.service';
import ParametricSearchService from './service/parametric-search.service';
import ParametricBuilderService from './service/parametric-builder.service';
import NotifyingService from './service/notifying.service';
import NavigationService from './service/navigation.service';
import ImageProcessorService from './service/image-processor.service';
import BrowseService from './components/browse/browse.service';
import DistributorService from './components/distributor/distributor.service';
import MatchDefinitionsFilter from './filter/match-definitions.filter';
import TechnologyService from './components/technology/technology.service';
import ParameterSupportService from './service/parameter-support.service';
import ErrorInterceptor from './interceptor/error.interceptor';
// environments
import envCw from './environment/env_cw';
import envDev from './environment/env_dev';
import envProd from './environment/env_prod';
import envTokin from './environment/env_tokin';
import envTest from './environment/env_test';
import routing from './config/app-routes.config';
import paginationTemplate from './config/dir-pagination-template.config';

let env = {};

// eslint-disable-next-line no-undef
switch (ENV) { // comes from webpack
    case 'cw' :
        env = {...envCw}; // currently no need for production
        break;
    case 'dev':
        env = {...envDev};
        break;
    case 'prod':
        env = {...envProd};
        break;
    case 'dev-tokin':
        env = {...envTokin}; // currently no difference between dev and prod tokin
        break;
    case 'prod-tokin':
        env = {...envTokin}; // currently no difference between dev and prod tokin
        break;
    case 'test':
        env = {...envTest};
        break;
    case 'local':
        // Do localhost like this, since CI doesn't care about it, but if imported will try to resolve it.
        env = {...require('./environment/env_localhost').default};
        break;
    default:
        console.error('Something went wrong when parsing the Env, defaulting to Dev!');
        env = {...envDev}; // something wrong was passed in
        break;
}

export const APP_NAME = 'ce-app';
console.log('loading app');
export const APP_MODULE = angular.module(APP_NAME, [
    // 1st party
    ngRouter,
    ngSanitize,
    ngSlick,
    ngTouch,
    ngTranslate,
    ngAnimate,
    // Our modules
    FileModule,
    APIModule,
    CacheModule,
    TransitionModule,
    LoadingModule,
    ModelModule,
    FormatterModule,
    // 3rd party
    'angularUtils.directives.dirPagination', // included globally in vendor.
    'ui.bootstrap', // included globally in vendor.
]);

APP_MODULE
// Set up the constants
    .constant('__env', env)
    .constant('APP_ERROR_CODES', APP_ERROR_CODES)
    .constant('APP_ROUTES', APP_ROUTES_DEFINITION)

    // define configuration
    .config(disableLogging)
    .config(translations)
    .config(routing)
    .config(paginationTemplate)
    .config( [
        '$compileProvider',
        function( $compileProvider )
        {
            $compileProvider.aHrefSanitizationWhitelist(/^\s*(https?|ftp|mailto|javascript):/);
        }
    ])

    // define components (have views implicitly tied to them)
    .controller(HomeComponent.name, HomeComponent)
    .controller(TechnologyComponent.name, TechnologyComponent)
    .controller(DistributorComponent.name, DistributorComponent)
    .controller(BrowseComponent.name, BrowseComponent)


    // define controllers (do not necessarily have views explicitly tied to them)
    .controller(CompareComponent.name, CompareComponent)
    .controller(SelectedFiltersComponent.name, SelectedFiltersComponent)
    .controller(HeaderController.name, HeaderController)
    .controller(DistributorPricingModalController.name, DistributorPricingModalController)
    .controller(BuilderController.name, BuilderController)
    .controller(DefinitionController.name, DefinitionController)
    .controller(ExternalSearchExecutionController.name, ExternalSearchExecutionController)
    .controller(MessageController.name, MessageController)
    .controller(QueueController.name, QueueController)
    .controller(SearchBarController.name, SearchBarController)

    // define directives
    .directive('builderSideBar', BuilderSidebarDirective)
    .directive('messageHandler', MessageDirective)
    .directive('queueBox', QueueDirective)
    .directive('bindHtmlCompile', BindHtmlCompileDirective)
    .directive('ceRepeat', CERepeatDirective)
    .directive('checkImage', CheckImageDirective)
    .directive('equalHeights', EqualHeightsDirective)
    .directive('focusInput', FocusDirective)
    .directive('longStringFixer', LongStringFixerDirective)
    .directive('stickyElement', StickyClassDirective)
    .directive('transitionEnd', TransitionEndDirective)

    // define filters
    .filter('distributorsFilter', DistributorsFilter)
    .filter('encodeURIComponent', EncodeURIComponentFilter)
    .filter('escape', EscapeFilter)
    .filter('formatPart', FormatPartFilter)
    .filter('startsWith', StartsWithFilter)
    .filter('matchDefinitions', MatchDefinitionsFilter)

    // factory definitions
    .factory(SleuthService.name, SleuthService)
    .factory(SearchExecutorService.name, SearchExecutorService)
    .factory(ParametricSearchService.name, ParametricSearchService)
    .factory(ParametricBuilderService.name, ParametricBuilderService)
    .factory(NotifyingService.name, NotifyingService)
    .factory(NavigationService.name, NavigationService)
    .factory(ImageProcessorService.name, ImageProcessorService)
    .factory(BrowseService.name, BrowseService)
    .factory(DistributorService.name, DistributorService)
    .factory(TechnologyService.name, TechnologyService)
    .factory('ParameterSupportService', ParameterSupportService)

    // service definitions
    .service(SelectedFiltersService.name, SelectedFiltersService)

    // interceptors
    .factory(ErrorInterceptor.name, ErrorInterceptor)

    // run/bootstrap our app module
    .run(bootstrapApp);

// additional needed bindings for when the window loads.
window.onload = loadBindings;

export default APP_NAME;
